import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ListMeta, Query } from '@/lib/api'
import { useApi } from '@/lib/api-context'

/**
 * Dashboard (docs/api/dashboard.md; regras em docs/areas/dashboard.md, RN-DSH-01 a 30). A fila de
 * trabalho e a agenda do dia são sempre ao vivo (RN-DSH-17); o resultado do período vem com a
 * evolução (T32) e pode ter até 5 minutos. O período é `week` · `month` · `quarter`, padrão `week`.
 */

export type Period = 'week' | 'month' | 'quarter'
export const PERIODS: Period[] = ['week', 'month', 'quarter']

export type PendingLevel = 'critical' | 'attention'
export type PendingArea = 'documents' | 'treatment_plans' | 'anamneses'
export type PendingType =
  | 'procedure_done_unsigned'
  | 'link_expired'
  | 'not_signed'
  | 'awaiting_clinic_signature'
  | 'signature_overdue'
  | 'document_generated_not_sent'
  | 'anamnesis_not_completed'
  | 'treatment_plan_without_product'
  | 'treatment_plan_lot_pending'
  | 'treatment_plan_photos_incomplete'

export type PendingItemAction =
  | { label: string; type: 'navigate'; route: string }
  | { label: string; type: 'resend'; appointmentIds: string[] }

export interface PendingItem {
  id: string
  type: PendingType
  level: PendingLevel
  area: PendingArea
  patientId: string
  name: string
  target: { type: 'appointment' | 'treatment_plan' | 'anamnesis'; id: string }
  timing: { since: string; days: number; time?: string; label: string }
  action: PendingItemAction
}

/** Uma linha do bloco "Exige sua atenção" (RN-DSH-14). */
export interface PendingItemGroup {
  type: PendingType
  level: PendingLevel
  area: PendingArea
  count: number
  text: string
  action: PendingItemAction
  items: PendingItem[]
}

export interface PendingMeta {
  total: number
  byLevel: { critical: number; attention: number }
}

export interface PeriodMetrics {
  grossRevenueCents: number
  refunds: { amountCents: number; count: number }
  netRevenueCents: number
  averageAppointmentValueCents: number
  billedAppointments: number
  patientsServed: number
  averageAge: number | null
  /** O valor dos contratos emitidos no período, pela data da geração (P-DSH-15). Não é receita. */
  generated: { amountCents: number; count: number; patients: number }
  /** O que entrou na clínica no período (T32). */
  movement: { newPatients: number; anamnesesCompleted: number; documentsGenerated: number }
}

export interface SeriesPoint {
  from: string
  to: string
  label: string
  generatedCents: number
  generatedCount: number
  netRevenueCents: number
  billedAppointments: number
  newPatients: number
  anamnesesCompleted: number
  documentsGenerated: number
}

export interface PeriodResult extends PeriodMetrics {
  period: {
    period: Period
    from: string
    to: string
    label: string
    bucket: 'day' | 'week'
    isSaved: boolean
  }
  practitionerId: string | null
  contracts: {
    generated: number
    signed: number
    unsigned: number
    byStatus: Record<string, number>
  }
  previous: PeriodMetrics & { from: string; to: string }
  series: SeriesPoint[]
  /** `false` enquanto a área de assinaturas não existe: faturado e assinado ficam em zero. */
  signaturesAvailable: boolean
  note: string
  calculatedAt: string
}

export interface TodayAppointment {
  id: string
  time: string | null
  patient: { id: string; name: string }
  procedures: string[]
  practitioner: { id: string; name: string } | null
  type: 'procedure' | 'standalone'
  statusSummary: {
    level: 'attention' | 'done'
    text: string
    action: PendingItemAction | null
    morePendingItems: number
  }
}

export interface TodayResult {
  date: string
  label: string
  isToday: boolean
  title: string
  appointments: TodayAppointment[]
}

export interface ClinicRow {
  id: string
  name: string
  city: string
  state: string
  pendingItems: { total: number; critical: number; attention: number }
  netRevenueCents: number
  billedAppointments: number
  generated: PeriodMetrics['generated']
  movement: PeriodMetrics['movement']
  lastAppointmentDate: string | null
  route: string
}

export interface ClinicsMeta extends ListMeta {
  period: { period: Period; label: string; isSaved: boolean }
  calculatedAt: string
}

export interface PeriodParams {
  period?: Period
  practitionerId?: string
}

export interface ClinicsParams {
  period?: Period
  search?: string
  page?: number
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  pending: ['dashboard', 'pending'] as const,
  period: (params: PeriodParams) => ['dashboard', 'period', params] as const,
  today: (date: string | undefined) => ['dashboard', 'today', date ?? 'today'] as const,
  clinics: (params: ClinicsParams) => ['dashboard', 'clinics', params] as const,
}

/** A fila de trabalho, sempre ao vivo (RN-DSH-17). */
export function usePendingItems(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: dashboardKeys.pending,
    queryFn: () => api.list<PendingItemGroup, PendingMeta>('/dashboard/pending-items'),
    enabled: options.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/** O resultado do período; os últimos números ficam na tela enquanto o novo cálculo não chega. */
export function usePeriod(params: PeriodParams, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: dashboardKeys.period(params),
    queryFn: () => api.get<PeriodResult>('/dashboard/period', params as Query),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  })
}

/** A agenda do dia, sempre ao vivo. */
export function useToday(date?: string, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: dashboardKeys.today(date),
    queryFn: () => api.get<TodayResult>('/dashboard/today', date ? { date } : {}),
    enabled: options.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/** Salva o período por pessoa e clínica (RN-DSH-24). */
export function useSavePeriod() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (period: Period) =>
      api.put<{ period: Period; updatedAt: string }>('/dashboard/preferences', { period }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
  })
}

/** O consolidado clínica a clínica, para o root sem clínica (RN-DSH-23). */
export function useClinics(params: ClinicsParams, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: dashboardKeys.clinics(params),
    queryFn: () =>
      api.list<ClinicRow, ClinicsMeta>('/dashboard/clinics', {
        period: params.period,
        search: params.search,
        page: params.page ?? 1,
        perPage: 25,
      }),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  })
}

/** O CSV do resultado do período (RN-DSH-25). */
export function useExportCsv() {
  const api = useApi()
  return useMutation({
    mutationFn: (params: PeriodParams) => api.download('/dashboard/export', params as Query),
  })
}
